import { t } from '@lingui/macro';
import { formatList } from '@luminovo/commons';
import { QuoteRequestField, QuoteRequestFieldRuntype } from '@luminovo/http-client';
import { formatQuoteRequestField } from '../formatters';

/**
 * These fields are always required, they cannot be hidden or made optional by the user.
 */
export const alwaysRequiredFields: QuoteRequestField[] = [
    'Bid' as const,
    'MinimumOrderQuantity' as const,
    'MinimumPackagingQuantity' as const,
    'OfferedManufacturer' as const,
    'OfferedMpn' as const,
    'UnitPrice' as const,
    'UnitPriceCurrency' as const,
    'UnitPriceQuantity' as const,
].sort();

/**
 * A small immutable datastructure to manage quote request field and makes sure that the following invariants are maintained:
 *
 * 1. All fields are assigned to one of the three categories: required, hidden, optional
 * 2. No field is assigned to more than one category
 * 3. The optional fields are always inferred as the fields that are not required or hidden
 * 4. The always required fields are always in the required category
 *
 *
 */
export class QuoteRequestConfigurationFields {
    private _required: Set<QuoteRequestField>;
    private _hidden: Set<QuoteRequestField>;
    private _allFields = QuoteRequestFieldRuntype.options.map((field) => field.value).sort();
    private _optional: Set<QuoteRequestField>;

    constructor({ required, hidden }: { required?: QuoteRequestField[]; hidden?: QuoteRequestField[] }) {
        // Start by setting the required fields
        this._required = new Set([...alwaysRequiredFields, ...(required || [])]);

        // Then set the hidden fields
        this._hidden = new Set(hidden || []);
        // In case the user tried to hide a required field, remove it from the hidden set
        this._required.forEach((field) => this._hidden.delete(field));

        // Then calculate the optional fields
        this._optional = new Set(
            this._allFields.filter((field) => !this._required.has(field) && !this._hidden.has(field)),
        );
    }

    getFormField(field: QuoteRequestField): FormFieldConfiguration {
        return {
            required: this._required.has(field),
            visible: !this._hidden.has(field),
        };
    }

    // TODO: restore this once we've added all the missing fields in the backend
    // formFieldsConfiguration(): FormFieldsConfiguration {
    //     return {
    //         ipn: this.getFormField('Ipn'),
    //         requestedPart: this.getFormField('Mpn'),
    //         partDescription: this.getFormField('Description'),
    //         requiredQuantity: this.getFormField('RequiredQuantity'),
    //         potentialQuantity: this.getFormField('PotentialQuantity'),
    //         recipients: this.getFormField('Recipient'),
    //         targetPrice: this.getFormField('TargetPrice'),
    //         customerName: this.getFormField('Customer'),

    //         validFor: { required: true, visible: false },

    //         validFrom: this.getFormField('ValidFrom'),
    //         validUntil: this.getFormField('ValidUntil'),
    //         cancellationWindow: this.getFormField('CancellationWindow'),
    //         cancellationTimeUnit: this.getFormField('CancellationTimeUnit'),
    //         itemClass: this.getFormField('ItemClass'),
    //         oneTimeCost: this.getFormField('OneTimeCost'),

    //         bid: this.getFormField('Bid'),
    //         currency: this.getFormField('UnitPriceCurrency'),
    //         unitPrice: this.getFormField('UnitPrice'),
    //         moq: this.getFormField('MinimumOrderQuantity'),
    //         mpq: this.getFormField('MinimumPackagingQuantity'),
    //         packaging: this.getFormField('Packaging'),
    //         standardFactoryLeadTime: this.getFormField('LeadTime'),
    //         standardFactoryLeadTimeUnit: this.getFormField('LeadTimeUnit'),
    //         notes: this.getFormField('AdditionalNotes'),
    //         stock: this.getFormField('Stock'),
    //         ncnr: this.getFormField('Ncnr'),
    //         part: this.getFormField('OfferedMpn'),
    //         supplierPartNumber: this.getFormField('SupplierPartNumber'),

    //         reach: this.getFormField('Reach'),
    //         rohs: this.getFormField('Rohs'),
    //         eccnNumbers: this.getFormField('EccnNumbers'),
    //         htsCode: this.getFormField('HtsCode'),
    //         countryOfOrigin: this.getFormField('CountryOfOrigin'),
    //     };
    // }

    withRequired(required: QuoteRequestField[]): QuoteRequestConfigurationFields {
        return new QuoteRequestConfigurationFields({
            required,
            hidden: Array.from(this._hidden).filter((field) => !required.includes(field)),
        });
    }

    withHidden(hidden: QuoteRequestField[]): QuoteRequestConfigurationFields {
        const filteredRequired = Array.from(this._required).filter((field) => !hidden.includes(field));

        return new QuoteRequestConfigurationFields({
            required: filteredRequired,
            hidden,
        });
    }

    withOptional(optional: QuoteRequestField[]): QuoteRequestConfigurationFields {
        return new QuoteRequestConfigurationFields({
            required: Array.from(this._required).filter((field) => !optional.includes(field)),
            hidden: Array.from(this._hidden).filter((field) => !optional.includes(field)),
        });
    }

    get required(): ReadonlyArray<QuoteRequestField> {
        return Array.from(this._required).sort();
    }

    get hidden(): ReadonlyArray<QuoteRequestField> {
        return Array.from(this._hidden).sort();
    }

    get optional(): ReadonlyArray<QuoteRequestField> {
        return Array.from(this._optional).sort();
    }

    get all(): ReadonlyArray<QuoteRequestField> {
        return this._allFields;
    }

    /**
     * Returns all the fields except the ones that are "fixed" in the mandatory fields.
     */
    get selectable(): ReadonlyArray<QuoteRequestField> {
        return this._allFields.filter((field) => !alwaysRequiredFields.includes(field));
    }

    formatRequired(): string {
        const requiredWithoutMandatory = this.required.filter((field) => !alwaysRequiredFields.includes(field));
        if (requiredWithoutMandatory.length === 0) {
            return '-';
        }
        return formatList(
            requiredWithoutMandatory.map((field) => formatQuoteRequestField(field)),
            'en',
        );
    }

    formatHidden(): string {
        if (this.hidden.length === 0) {
            return '-';
        }
        return formatList(
            this.hidden.map((field) => formatQuoteRequestField(field)),
            'en',
        );
    }

    /**
     * There are 3 possible formats:
     * 1. When there are no hidden fields. Render as "default".
     * 2. If there is at least one hidden field and no changes to the mandatory required fields
     *    "A,B C hidden"
     * 3. If there is at least one hidden field and changes to the mandatory required fields
     *    "A,B, C required and D,E,F hidden"
     */
    format(): string {
        const requiredWIthoutMandatory = this.required.filter((field) => !alwaysRequiredFields.includes(field));
        if (this.hidden.length === 0 && requiredWIthoutMandatory.length === 0) {
            return t`All fields optional`;
        }

        const listHidden = this.formatHidden();
        const listMandatory = this.formatRequired();

        if (requiredWIthoutMandatory.length === 0) {
            return t`Hidden: ${listHidden}`;
        }

        return t`Required: ${listMandatory}; Hidden: ${listHidden}`;
    }
}

/**
 * @deprecated replace witn `QuoteRequestConfigurationFields`
 */
export interface FormFieldsConfiguration {
    ipn: FormFieldConfiguration;
    requestedPart: FormFieldConfiguration;
    partDescription: FormFieldConfiguration;
    requiredQuantity: FormFieldConfiguration;
    potentialQuantity: FormFieldConfiguration;
    recipients: FormFieldConfiguration;
    targetPrice: FormFieldConfiguration;
    customerName: FormFieldConfiguration;

    validFor: FormFieldConfiguration;

    validFrom: FormFieldConfiguration;
    validUntil: FormFieldConfiguration;
    cancellationWindow: FormFieldConfiguration;
    cancellationTimeUnit: FormFieldConfiguration;
    itemClass: FormFieldConfiguration;
    oneTimeCost: FormFieldConfiguration;

    bid: FormFieldConfiguration;
    currency: FormFieldConfiguration;
    unitPrice: FormFieldConfiguration;
    moq: FormFieldConfiguration;
    mpq: FormFieldConfiguration;
    packaging: FormFieldConfiguration;
    standardFactoryLeadTime: FormFieldConfiguration;
    standardFactoryLeadTimeUnit: FormFieldConfiguration;
    notes: FormFieldConfiguration;
    stock: FormFieldConfiguration;
    ncnr: FormFieldConfiguration;
    part: FormFieldConfiguration;
    supplierPartNumber: FormFieldConfiguration;

    reach: FormFieldConfiguration;
    rohs: FormFieldConfiguration;
    eccnNumbers: FormFieldConfiguration;
    htsCode: FormFieldConfiguration;
    countryOfOrigin: FormFieldConfiguration;
}

export type FormFieldConfiguration = {
    visible: boolean;
    required: boolean;
};
